/**
 * ScreenGuard — blocks a restricted screen (Admin Panel > Screens) for anyone
 * who may not open it, whichever way they got there: menu, bookmark, typed
 * address or an old link. Wraps every domain page in MainLayout. Menus hide
 * the same screens (useDomainPages); the server refuses their data when the
 * restriction lists its API addresses (backend/shared/auth/screens.js).
 *
 * `path` checks a different address than the one in the browser: Admin >
 * Data management shows /finance/data-management at /admin/data/finance, and
 * a restriction on the first must cover the second.
 */

import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function ScreenGuard({ children, path }) {
  const location = useLocation();
  const pathname = path || location.pathname;
  const { search } = location;
  const { canOpen, restrictionsFor } = useAuth();
  if (canOpen(pathname, search)) return children;
  const names = restrictionsFor(pathname, search).map((s) => s.screen_name).join(', ');
  return (
    <div className="p-10 flex justify-center">
      <div className="max-w-lg w-full bg-white border border-gray-200 rounded-lg p-6 text-center shadow-sm">
        <Lock className="w-8 h-8 mx-auto text-gray-400 mb-3" />
        <h1 className="text-lg font-semibold text-gray-900 mb-1">This screen is restricted</h1>
        <p className="text-sm text-gray-600">
          {names ? <><strong>{names}</strong> is </> : 'It is '}
          only open to Super Admin and the roles a Super Admin has chosen. If you need it, ask a Super Admin to give you
          a role that includes it (Admin Panel › Screens).
        </p>
        <Link to="/" className="inline-block mt-4 text-sm text-blue-700 hover:underline">Back to the home page</Link>
      </div>
    </div>
  );
}
